import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {CUSTOM_ELEMENTS_SCHEMA, Inject, ModuleWithProviders, NgModule, Optional} from '@angular/core';
import {CommonModule, DecimalPipe} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {SwiperModule} from 'swiper/angular';
import {BreadcrumbModule} from 'xng-breadcrumb';
import {EditorModule} from '@tinymce/tinymce-angular';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {NgbDatepickerModule} from '@ng-bootstrap/ng-bootstrap';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {NgxIntlTelInputModule} from 'ngx-intl-tel-input';
import {NgxPaginationModule} from 'ngx-pagination';
import {ClipboardModule} from '@angular/cdk/clipboard';

import {FooCommonModule} from 'common-framework';
import {DateRangePickerComponent} from './date-range-picker/date-range-picker.component';
import {DeleteDialogComponent} from './delete-dialog/delete-dialog.component';
import {SearchBarComponent} from './search-bar/search-bar.component';
import {TranslationInputComponent} from './translation-input/translation-input.component';
import {InfoDialogComponent} from './info-dialog/info-dialog.component';

import {ValidationService} from './services/validation.service';
import {TranslationService} from './services/translation.service';
import {CountryService} from './services/country.service';

import {FooTemplateDirective} from './foo-template.directive';
import {FooTemplatesService} from './foo-templates.service';
import {
  ControlValueAccessorConnectorComponent
} from './control-value-accessor-connector.component';
import {MessagePopupComponent} from './message-popup/message-popup.component';
import {NoDataComponent} from './no-data/no-data.component';
import {
  FooSearchableSelectComponent
} from './foo-searchable-select/foo-searchable-select.component';
import {FirstLoadComponent} from './first-load/first-load.component';


import {FileIconComponent} from './file-icon/file-icon.component';
import {BoxesFilterComponent} from './boxes-filter/boxes-filter.component';
import {FooCheckboxComponent} from './controls/foo-checkbox/foo-checkbox.component';
import {FooConsentComponent} from './controls/foo-consent/foo-consent.component';
import {FooControlComponent} from './controls/foo-control/foo-control.component';
import {FooControlErrorsComponent} from './controls/foo-control-errors/foo-control-errors.component';
import {FooControlLabelComponent} from './controls/foo-control-label/foo-control-label.component';
import {FooDatepickerComponent} from './controls/foo-datepicker/foo-datepicker.component';
import {FooFileComponent} from './controls/foo-file/foo-file.component';
import {FooInputComponent} from './controls/foo-input/foo-input.component';
import {FooMobileNumberComponent} from './controls/foo-mobile-number/foo-mobile-number.component';
import {FooNumberComponent} from './controls/foo-number/foo-number.component';
import {FooRadiolistComponent} from './controls/foo-radiolist/foo-radiolist.component';
import {FooSelectBoxComponent} from './controls/foo-select-box/foo-select-box.component';
import {FooTextareaComponent} from './controls/foo-textarea/foo-textarea.component';
import {DisplayAsHtmlComponent} from './display-as-html/display-as-html.component';
import {FooTimepickerComponent} from './controls/foo-timepicker/foo-timepicker.component';
import {CommonDialogService} from './services/common-dialog.service';
import {SendOldValuesService} from './services/send-old-values.service';
import {FooDatetimepickerComponent} from './controls/foo-datetimepicker/foo-datetimepicker.component';
import {DynamicListingComponent} from './dynamic-listing/dynamic-listing-component/dynamic-listing.component';
import {ListOfEntriesComponent} from './dynamic-listing/templates/list-of-entries/list-of-entries.component';
import {
  ListOfColumnsAsKeyValueComponent
} from './dynamic-listing/templates/list-of-columns-as-key-value/list-of-columns-as-key-value.component';
import {DynamicListingService} from './services/dynamic-listing.service';
import {ListingTemplateDirective} from "./dynamic-listing/listing-template.directive";

import {FooColorpickerComponent} from './controls/foo-colorpicker/foo-colorpicker.component';
import {TooltipMenuComponent} from './tooltip-menu/tooltip-menu.component';
import {FooTooltipComponent} from './foo-tooltip/foo-tooltip.component';
import {PageButtonsComponent} from './page-buttons/page-buttons.component';

import {
  FooPaginatedListingDialogComponent
} from './foo-paginated-listing-dialog/foo-paginated-listing-dialog.component';
import {
  APP_COMPONENTS_CONFIG,
  AppComponentsConfig,
  FooSharedModulesComponentsConfigService
} from './services/foo-shared-modules-components-config.service';
import {FooPaginatedSelectComponent} from './controls/foo-paginated-select/foo-paginated-select.component';
import {FooPaginatedSelectDialogComponent} from './foo-paginated-select-dialog/foo-paginated-select-dialog.component';
import {FooTagsInputComponent} from './controls/foo-tags-input/foo-tags-input.component';
import {FooIconComponent} from './foo-icon/foo-icon.component';
import {CountUpComponent} from './count-up/count-up.component';
import {SelectablePropertyViewerComponent} from './selectable-property-viewer/selectable-property-viewer.component';

const components = [
  DateRangePickerComponent,
  DeleteDialogComponent,
  InfoDialogComponent,
  SearchBarComponent,
  TranslationInputComponent,
  FooTemplateDirective,
  ControlValueAccessorConnectorComponent,
  MessagePopupComponent,
  NoDataComponent,
  FooSearchableSelectComponent,
  FirstLoadComponent,
  FileIconComponent,
  BoxesFilterComponent,
  FooCheckboxComponent,
  FooConsentComponent,
  FooControlComponent,
  FooControlErrorsComponent,
  FooControlLabelComponent,
  FooDatepickerComponent,
  FooFileComponent,
  FooInputComponent,
  FooMobileNumberComponent,
  FooNumberComponent,
  FooRadiolistComponent,
  FooSelectBoxComponent,
  FooTextareaComponent,
  FooColorpickerComponent,
  DisplayAsHtmlComponent,
  FooTimepickerComponent,
  FooDatetimepickerComponent,
  DynamicListingComponent,
  ListingTemplateDirective,
  ListOfEntriesComponent,
  ListOfColumnsAsKeyValueComponent,
  TooltipMenuComponent,
  FooTooltipComponent,
  PageButtonsComponent,
  FooPaginatedListingDialogComponent,
  FooPaginatedSelectComponent,
  FooPaginatedSelectDialogComponent,
  FooTagsInputComponent,
  FooIconComponent,
  CountUpComponent,
  SelectablePropertyViewerComponent
];

@NgModule({
  declarations: [
    ...components,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [
    ...components,
    TranslateModule,
    CommonModule,
    FooCommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    SwiperModule,
    BreadcrumbModule,
    EditorModule,
    DragDropModule,
    NgbDatepickerModule,
    NgbModule,
    NgxIntlTelInputModule,
    NgxPaginationModule,
    ClipboardModule
  ], imports: [TranslateModule,
    CommonModule,
    FooCommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    SwiperModule,
    BreadcrumbModule,
    EditorModule,
    DragDropModule,
    NgbDatepickerModule,
    NgbModule,
    NgxIntlTelInputModule,
    NgxPaginationModule,
    ClipboardModule], providers: [
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class SharedModule {
  static forRoot(componentConfig: AppComponentsConfig = {}): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        ValidationService,
        TranslationService,
        CountryService,
        FooTemplatesService,
        CommonDialogService,
        SendOldValuesService,
        DynamicListingService,
        DecimalPipe,
        FooSharedModulesComponentsConfigService,
        {provide: APP_COMPONENTS_CONFIG, useValue: componentConfig, multi: true},
      ]
    };
  }

  static forChild(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        ValidationService,
        CountryService,
        FooTemplatesService,
        CommonDialogService,
        SendOldValuesService,
        DynamicListingService,
        DecimalPipe,
        FooSharedModulesComponentsConfigService
      ]
    };
  }

  constructor(
    componentsConfig: FooSharedModulesComponentsConfigService,
    @Optional() @Inject(APP_COMPONENTS_CONFIG) componentsConfigs: AppComponentsConfig[],
  ) {

    if (componentsConfigs) {
      componentsConfigs.forEach((config: AppComponentsConfig) => {
        componentsConfig.setComponentsConfig(config?.configs);
      });
    }
  }
}
